import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Trophy, 
  Calendar,
  Users,
  ChevronRight,
  Medal,
  Table,
  Star
} from 'lucide-react';
import { formatCurrency } from '../lib/utils';

const getStatusBadge = (status) => {
  switch (status) {
    case 'registration':
      return { text: 'Anmeldung offen', className: 'bg-tunis-teal text-white' };
    case 'in_progress':
      return { text: 'Läuft', className: 'bg-amber-500 text-white animate-pulse' };
    case 'completed':
      return { text: 'Beendet', className: 'bg-slate-500 text-white' };
    default:
      return { text: status, className: 'bg-slate-500 text-white' };
  }
};

export default function Leagues() {
  const { user } = useAuth();
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeagues = async () => {
      try {
        const response = await api.get('/leagues');
        setLeagues(response.data || []);
      } catch (error) {
        console.error('Failed to fetch leagues:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLeagues();
  }, []);

  const openLeagues = leagues.filter(l => l.status === 'registration');
  const activeLeagues = leagues.filter(l => l.status === 'in_progress');
  const completedLeagues = leagues.filter(l => l.status === 'completed');

  const LeagueCard = ({ league }) => {
    const statusBadge = getStatusBadge(league.status);
    const leader = league.standings?.[0];
    
    return (
      <Link to={`/leagues/${league.id}`} data-testid={`league-${league.id}`}>
        <Card className="hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1 border-slate-200 overflow-hidden">
          <div className="h-2 bg-gradient-to-r from-purple-500 to-pink-500" />
          <CardContent className="p-5">
            <div className="flex justify-between items-start mb-3">
              <div>
                <Badge className={statusBadge.className}>
                  {statusBadge.text}
                </Badge>
                <Badge variant="outline" className="ml-2">
                  {league.season}
                </Badge>
              </div>
              <div className="text-right">
                <div className="text-sm text-muted-foreground">Preisgeld</div>
                <div className="font-bold text-purple-600">{formatCurrency(league.prize_pool)}</div>
              </div>
            </div>
            
            <h3 className="font-display font-bold text-xl text-tunis-blue mb-2">
              {league.name}
            </h3>
            
            <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
              {league.description}
            </p>
            
            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
              <div className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                {league.team_count}/{league.max_teams}
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {league.matches_per_team}x Hinrunde
              </div>
            </div>

            {leader && league.status !== 'registration' && (
              <div className="p-3 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border border-amber-200 mb-4">
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-amber-500" />
                  <span className="text-sm font-medium text-amber-700">
                    Tabellenführer: {leader.team_name} ({leader.points} Pkt.)
                  </span>
                </div>
              </div>
            )}

            <div className="flex justify-between items-center pt-4 border-t border-slate-100">
              <div className="text-sm">
                <span className="text-muted-foreground">Eintritt:</span>{' '}
                <span className="font-bold text-tunis-blue">{formatCurrency(league.entry_fee)}</span>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </div>
          </CardContent>
        </Card>
      </Link>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-8 w-48 mb-8" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-64 rounded-2xl" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="leagues-page">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl mb-4 shadow-lg">
            <Table className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-display font-bold text-2xl sm:text-3xl text-tunis-blue">
            Hobby-Ligen
          </h1>
          <p className="text-muted-foreground mt-2">Spiele eine ganze Saison mit deinem Team</p>
        </div>

        {/* Info Banner */}
        <Card className="bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0 mb-8">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-1">
                <h3 className="font-bold text-xl mb-2">Was ist eine Hobby-Liga?</h3>
                <p className="text-white/90">
                  Melde dein Team an und spiele über mehrere Wochen gegen andere Teams. 
                  Jeder spielt gegen jeden - Punkte sammeln, Tabelle erklimmen, Meister werden!
                </p>
              </div>
              <div className="flex gap-4">
                <div className="text-center">
                  <div className="font-bold text-3xl">3</div>
                  <div className="text-white/80 text-sm">Punkte/Sieg</div>
                </div>
                <div className="text-center">
                  <div className="font-bold text-3xl">1</div>
                  <div className="text-white/80 text-sm">Punkt/Remis</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="open">
          <TabsList className="mb-6">
            <TabsTrigger value="open" data-testid="tab-open-leagues">
              Offen ({openLeagues.length})
            </TabsTrigger>
            <TabsTrigger value="active" data-testid="tab-active-leagues">
              Aktiv ({activeLeagues.length})
            </TabsTrigger>
            <TabsTrigger value="completed" data-testid="tab-completed-leagues">
              Beendet ({completedLeagues.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="open">
            {openLeagues.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {openLeagues.map((league) => (
                  <LeagueCard key={league.id} league={league} />
                ))}
              </div>
            ) : (
              <Card className="border-slate-200">
                <CardContent className="py-12 text-center">
                  <Table className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg text-tunis-blue mb-2">
                    Keine offenen Ligen
                  </h3>
                  <p className="text-muted-foreground">
                    Derzeit sind keine Ligen zur Anmeldung verfügbar.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="active">
            {activeLeagues.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {activeLeagues.map((league) => (
                  <LeagueCard key={league.id} league={league} />
                ))}
              </div>
            ) : (
              <Card className="border-slate-200">
                <CardContent className="py-12 text-center">
                  <Table className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-muted-foreground">Keine aktiven Ligen</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="completed">
            {completedLeagues.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {completedLeagues.map((league) => (
                  <LeagueCard key={league.id} league={league} />
                ))}
              </div>
            ) : (
              <Card className="border-slate-200">
                <CardContent className="py-12 text-center">
                  <Medal className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-muted-foreground">Noch keine beendeten Ligen</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
